import { css } from 'styled-components';
import { getContrast, darken, rgba } from 'polished';

import colors from '../../../assets/styled/colors';

import { newTableTokens } from '../../../tokens/components/NewTable/tokens';
import { elevationTokens, colorTokens, fontTokens } from '../../../tokens/globals/tokens';

import CoreRow from './CoreRow';
import { StyledOrderableRow } from '../components/NewTableOrderableRow/StyledOrderableRow';
import { StyledExpandAll } from '../components/NewTableTwiddle/components/TwiddleExpandAll/TwiddleExpandAll';

/**
 * Ensures text color will contrast with the background color by default
 * @param bgColor Hex code of background color
 * @returns Hex code of contrasting color (black or white)
 */
export function setContrastColor(bgColor: string): string {
  const optimalContrastRatio = 4.5;
  let color: '#ffffff' | '#1c2222' = colorTokens.GlobalBrandWhite;

  if (getContrast(bgColor, color) < optimalContrastRatio) {
    color = colorTokens.GlobalBrandGray100;
  }

  return color;
}

export const hoverDarkenFactor = 0.03;

const headerDensity = {
  normal: css`
    padding: ${newTableTokens.HeaderSpacingVerticalBaseDefault} ${newTableTokens.HeaderSpacingHorizontalBaseDefault};
  `,
  low: css`
    padding: ${newTableTokens.HeaderSpacingVerticalLowDefault} ${newTableTokens.HeaderSpacingHorizontalLowDefault};
  `,
  high: css`
    padding: ${newTableTokens.ContainerSpacingVerticalHighDefault}
      ${newTableTokens.ContainerSpacingHorizontalHighDefault};
    font-size: ${newTableTokens.ContainerFontSizeHighDefault};
  `,
  higher: css`
    padding: ${newTableTokens.ContainerSpacingVerticalHigherDefault}
      ${newTableTokens.ContainerSpacingHorizontalHigherDefault};
    font-size: ${newTableTokens.ContainerFontSizeHigherDefault};
  `,
} as const;

const cellDensity = {
  normal: css`
    padding: ${newTableTokens.CellSpacingVerticalBaseDefault} ${newTableTokens.CellSpacingHorizontalBaseDefault};
    font-size: ${newTableTokens.ContainerFontSizeBaseDefault};
  `,
  low: css`
    padding: ${newTableTokens.CellSpacingVerticalLowDefault} ${newTableTokens.CellSpacingHorizontalLowDefault};
  `,
  high: css`
    padding: ${newTableTokens.ContainerSpacingVerticalHighDefault}
      ${newTableTokens.ContainerSpacingHorizontalHighDefault};
    font-size: ${newTableTokens.ContainerFontSizeHighDefault};
  `,
  higher: css`
    padding: ${newTableTokens.ContainerSpacingVerticalHigherDefault}
      ${newTableTokens.ContainerSpacingHorizontalHigherDefault};
    font-size: ${newTableTokens.ContainerFontSizeHigherDefault};
  `,
} as const;

// Box-shadows are used to create borders because we use border-collapse
const cellBorders = {
  topFoot: `inset 0 1px ${newTableTokens.CellBorderColorBaseDefault}`,
  bottomFoot: `inset 0 -1px ${newTableTokens.CellBorderColorBaseDefault}`,

  bottomCell: `1px solid ${newTableTokens.CellBorderColorBaseDefault}`,
  bottomHead: `inset 0 -1px ${newTableTokens.HeaderBorderColorBaseDefault}`,

  leftDivider: `inset 1px 0 ${newTableTokens.CellBorderColorDividerDefault}`,
  rightDivider: `inset -1px 0 ${newTableTokens.CellBorderColorDividerDefault}`,

  rightFrozen: `inset -1px 0 ${newTableTokens.CellBorderColorBaseDefault}`,
};

const tableVariants = {
  'feedback-positive': newTableTokens.RowBackgroundColorPositiveDefault,
  'feedback-notice': newTableTokens.RowBackgroundColorNoticeDefault,
  'feedback-negative': newTableTokens.RowBackgroundColorNegativeDefault,
  'feedback-informational': newTableTokens.RowBackgroundColorInformationalDefault,
  'feedback-neutral': newTableTokens.RowBackgroundColorNeutralDefault,
} as const;

/*
  Priority of background color (any preset > any bgColor):
    - Cell preset: this is ink's opinion on the cell (i.e. keys in a totals row)
    - Row preset: this is ink's opinion on the row (i.e totals)
    - Cell bgColor: can overwrite specific cell of choice in a row (i.e. blue cell on red row)
    - Row bgColor: sets every cell (i.e. red)

  Priority of cell zindex (to support sticky header). They'll be updated with semantic tokens in the future.
    - subheader - set at 1 now and the component is not released
    - cell + `freeze` (5)
    - header (10)
    - header + `freeze` (15)

  Borders:
    - Bottom borders are created with border
      - Should only go in one direction, downwards
      - TH cells use box-shadow (due to overlap issues in Orderable tables)
      - Orderable tables are an exception
    - Left and Right borders are created with box-shadow
*/

export const styles = {
  cell: css`
    transition: all 60ms ease-in;

    // Border bottom is only used in tbody only
    // This also creates a border for when tfoot doesn't exist
    border-bottom: ${cellBorders.bottomCell};

    // Remove all borders from last row cells
    ${CoreRow}:last-child && {
      border-bottom: 0;
    }

    // Add it back if no footer (we could use :not but it's expensive?)
    tbody:last-child ${CoreRow}:last-child && {
      border-bottom: ${cellBorders.bottomCell};
    }

    tfoot && {
      box-shadow: ${cellBorders.topFoot}, ${cellBorders.bottomFoot};
      border-bottom: 0;

      ${({ theme }) =>
        theme.cellDivider &&
        css`
          box-shadow: ${cellBorders.topFoot}, ${cellBorders.bottomFoot},
            ${theme.cellDivider === 'right' ? cellBorders.rightDivider : cellBorders.leftDivider};
        `}
    }

    ${({ theme }) => cellDensity[theme.density]};

    ${({ theme }) =>
      theme.cellBgColor &&
      css`
        background-color: ${colors[theme.cellBgColor]};
        color: ${setContrastColor(colors[theme.cellBgColor])};
      `}

    ${({ theme }) =>
      theme.rowVariant &&
      css`
        background-color: ${tableVariants[theme.rowVariant]};
      `}

    ${({ theme }) =>
      theme.cellVariant &&
      css`
        background-color: ${tableVariants[theme.cellVariant]};
      `}

    ${({ theme }) =>
      theme.rowSelected &&
      css`
        background-color: ${newTableTokens.RowBackgroundColorSelectedDefault};
      `}


    ${({ theme }) =>
      (theme.twiddleLevel || theme.rowIndent) &&
      css`
        &:first-child {
          padding-left: calc(
            ${newTableTokens.CellSpacingHorizontalBaseDefault} * ${theme.twiddleLevel || theme.rowIndent}
          );
        }
      `}

    ${({ theme }) =>
      theme.rowHasClick &&
      css`
        ${CoreRow}:hover && {
          background-color: ${darken(hoverDarkenFactor, colors[theme.cellBgColor])};
        }
      `}

    // Applies color only if there is no cell divider (colored)
    // Left and right cell borders are created with box-shadow
    ${({ theme }) =>
      theme.cellLastFrozenColumn &&
      !theme.cellDivider &&
      css`
        box-shadow: ${cellBorders.rightFrozen};
      `}
  `,
  // Applies styles to table header cells
  header: css`
    background-color: ${newTableTokens.HeaderBackgroundColorBaseDefault};
    box-shadow: ${cellBorders.bottomHead};
    font-weight: 500;
    position: sticky;
    top: 0;
    vertical-align: bottom;
    z-index: ${elevationTokens.GlobalNewTableHeaderInitial};

    ${({ theme }) => headerDensity[theme.density]};

    ${({ theme }) =>
      theme.cellFreeze &&
      css`
        z-index: ${elevationTokens.GlobalNewTableHeaderFrozen};
      `}

    ${({ theme }) =>
      theme.cellDivider &&
      css`
        box-shadow: ${cellBorders.bottomHead},
          ${theme.cellDivider === 'right' ? cellBorders.rightDivider : cellBorders.leftDivider};
      `}

    ${({ theme }) =>
      theme.cellLastFrozenColumn &&
      css`
        box-shadow: ${cellBorders.bottomHead}, ${cellBorders.rightFrozen};

        ${theme.cellDivider &&
        css`
          box-shadow: ${cellBorders.bottomHead},
            ${theme.cellDivider === 'right' ? cellBorders.rightDivider : cellBorders.leftDivider};
        `}
      `}

    // Headers do not require setting a prop but there are only 2 presets
    // where icons need micro adjustments to align vertically with the text.
    & ${StyledExpandAll},
    & label[data-testid='checkbox-icon-label'] {
      ${({ theme }) =>
        theme.density &&
        css`
          margin-bottom: 1px;
        `}
    }

    // Higher density has less horizontal padding
    & ${StyledExpandAll} {
      ${({ theme }) =>
        theme.density &&
        theme.density === 'higher' &&
        css`
          margin-left: -2px;
        `}
    }
  `,
  subheader: css`
    padding: 3px ${newTableTokens.HeaderSpacingHorizontalBaseDefault} 4px;
    position: relative;
    z-index: 1;
  `,
  orderableCell: css`
    ${({ theme }) =>
      theme.isDragging &&
      css`
        border-bottom-color: ${colorTokens.GlobalBrandBlue70};
      `}

    ${({ theme }) =>
      theme.isDraggingOver &&
      !theme.isDragging &&
      css`
        border-bottom-color: transparent;
        box-shadow: inset 0 1px 0 0 ${newTableTokens.CellBorderColorBaseDefault};

        &:last-child {
          box-shadow: inset 0 -1px 0 0 ${newTableTokens.CellBorderColorBaseDefault};
        }
      `}

    /*
      All the cells need a top and bottom border to make a full border effect
      around the row. We can't use border and outline because those shift the table with additional pixels.
    */
    ${StyledOrderableRow}:hover &&,
    ${StyledOrderableRow}:focus-visible && {
      box-shadow: inset 0 1px 0 0 ${colorTokens.GlobalBrandGray40}, inset 0 -1px 0 0 ${colorTokens.GlobalBrandGray40};

      // Dividers need 3 borders to create the full border effect
      ${({ theme }) =>
        theme.cellDivider &&
        css`
          box-shadow: inset 0 1px 0 0 ${colorTokens.GlobalBrandGray40},
            inset 0 -1px 0 0 ${colorTokens.GlobalBrandGray40},
            inset ${theme.cellDivider === 'right' ? '-1px' : '1px'} 0 ${colorTokens.GlobalBrandBlue70};
        `}

      /* This is the first column and needs a shadow on the left */
      ${({ theme }) =>
        theme.cellPreset === 'dragHandleIcon' &&
        css`
          color: ${colorTokens.GlobalBrandBlue70};
          box-shadow: inset 1px 1px 0 0 ${colorTokens.GlobalBrandGray40},
            inset 0 -1px 0 0 ${colorTokens.GlobalBrandGray40};
        `}

      /* Frozen cells are !important in CoreCell, so we have to overwrite it */
      ${({ theme }) =>
        theme.cellPreset !== 'dragHandleIcon' &&
        theme.cellFreeze &&
        css`
          box-shadow: inset 0 1px 0 0 ${colorTokens.GlobalBrandGray40},
            inset 0 -1px 0 0 ${colorTokens.GlobalBrandGray40}, inset -2px 0 ${newTableTokens.CellBorderColorBaseDefault} !important;
        `}

      /* The last column needs a shadow to the right */
      &:last-child {
        box-shadow: inset -1px 1px 1px 0 ${colorTokens.GlobalBrandGray40},
          inset 0 -1px 0 0 ${colorTokens.GlobalBrandGray40};
      }
    }

    ${StyledOrderableRow}:focus-visible && {
      ${({ theme }) =>
        theme.isDraggingOver &&
        css`
          box-shadow: none;
          &:first-child,
          &:last-child {
            box-shadow: none;
          }
        `}
    }

    ${StyledOrderableRow} && {
      ${({ theme }) =>
        theme.isDraggingOver &&
        theme.cellPreset === 'dragHandleIcon' &&
        css`
          color: ${colorTokens.GlobalBrandGray30};
          ${({ theme }) =>
            theme.isDragging &&
            css`
              color: ${colorTokens.GlobalBrandBlue70};
            `}
        `}
    }
  `,
} as const;

export const cellPresets = {
  twiddleIcon: css`
    min-width: 40px;
    width: 40px;
    padding-right: 0;
  `,
  action: css`
    text-align: right;
    width: 40px;
    white-space: nowrap;
  `,
  checkbox: css`
    min-width: 40px;
    padding-right: 0;
    width: 40px;
  `,
  dragHandleIcon: css`
    min-width: 20px;
    padding: 0 0 0 8px;
    width: 24px;
    color: ${colorTokens.GlobalBrandGray50};
  `,
  radio: css`
    min-width: 40px;
    padding-right: 0;
    width: 40px;
  `,
  editable: css`
    background-color: ${colorTokens.GlobalBrandWhite};
    padding: 0;

    ${({ theme }) => css`
      /*
        Prevents editable cells to overlap with Ribbon and Foot,
        except when the cell is frozen.
      */
      z-index: ${theme.cellFreeze ? elevationTokens.GlobalNewTableCellFrozen : '0'};
    `}

    ${({ theme }) =>
      theme.rowHasClick &&
      css`
        ${CoreRow}:hover && {
          background-color: ${({ theme }) =>
            theme.cellBgColor ? colors[theme.cellBgColor] : colorTokens.GlobalBrandWhite};
        }
      `}
  `,
  key: css`
    background-color: ${newTableTokens.CellBackgroundColorKeyDefault};
    font-weight: ${fontTokens.GlobalWeightBody3};
    color: ${setContrastColor(colorTokens.GlobalBrandGray10)};

    ${({ theme }) =>
      theme.rowHasClick &&
      css`
        ${CoreRow}:hover && {
          background-color: ${newTableTokens.CellBackgroundColorNeutralHover};
        }
      `}
  `,
  totals: css`
    background-color: ${newTableTokens.RowBackgroundColorTotalDefault};
    font-weight: ${fontTokens.GlobalWeightBody2};
    color: ${setContrastColor(colorTokens.GlobalBrandBlue30)};

    ${({ theme }) =>
      theme.rowHasClick &&
      css`
        ${CoreRow}:hover && {
          background-color: ${newTableTokens.RowBackgroundColorTotalHover};
        }
      `}
  `,
  lowContrast: css`
    background-color: ${colorTokens.GlobalBrandGray10};
    color: ${rgba(colorTokens.GlobalBrandGray50, 0.65)};
    -webkit-text-fill-color: ${rgba(colorTokens.GlobalBrandGray50, 0.65)};

    ${({ theme }) =>
      theme.rowHasClick &&
      css`
        ${CoreRow}:hover && {
          background-color: ${darken(hoverDarkenFactor, colorTokens.GlobalBrandGray10)};
        }
      `}
  `,
} as const;
